import type { Lang } from '../i18n';
import type { CatalogStation } from './catalog';
import type { SortMode } from './settings';
import type { Station, StationSource } from './stations';

/**
 * Knjižnica postaj — čista logika nad `Station[]` (T1 §2).
 *
 * Nobena funkcija tu ne mutira vhoda in nobena ne pozna shrambe ne DOM-a.
 * Razlog ni čistost zaradi čistosti: seznam postaj je edini nabor, ki ga
 * uporabnik ureja, in vsaka od teh operacij mora biti preverljiva brez
 * brskalnika, sicer se napaka v vrstnem redu pokaže šele na napravi.
 */

/* ── Branje iz shrambe ─────────────────────────────────────────────── */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const SOURCES: readonly StationSource[] = ['radio-browser', 'webradiodb', 'manual', 'seed'];

/**
 * Postaja iz shrambe, z zavrnitvijo vsega, kar ni uporabno.
 *
 * `id`, `name` in `url` so obvezni — postaja brez njih ni okrnjena, ampak
 * nesmiselna: ni je mogoče niti predvajati niti pokazati. Vse ostalo dobi
 * privzeto vrednost, ker je manjkajoč `bitrate` samo neznanka, ne okvara.
 */
function parseStation(raw: unknown, index: number): Station | null {
  if (!isRecord(raw)) return null;
  if (typeof raw.id !== 'string' || raw.id === '') return null;
  if (typeof raw.name !== 'string' || raw.name === '') return null;
  if (typeof raw.url !== 'string' || raw.url === '') return null;

  const url = raw.url;
  const resolved = typeof raw.urlResolved === 'string' ? raw.urlResolved : undefined;

  const station: Station = {
    id: raw.id,
    name: raw.name,
    url,
    tags: Array.isArray(raw.tags) ? raw.tags.filter((t): t is string => typeof t === 'string') : [],
    isHls: raw.isHls === true,
    // Izpeljano, ne prebrano: zapis in naslov se lahko razideta ob ročnem
    // urejanju ali uvozu, naslov pa je tisti, ki dejansko odloča.
    isHttps: (resolved ?? url).startsWith('https:'),
    favorite: raw.favorite === true,
    order: typeof raw.order === 'number' ? raw.order : index,
    addedAt: typeof raw.addedAt === 'number' ? raw.addedAt : 0,
    playCount: typeof raw.playCount === 'number' ? raw.playCount : 0,
    source: SOURCES.includes(raw.source as StationSource)
      ? (raw.source as StationSource)
      : 'manual',
  };

  if (resolved !== undefined) station.urlResolved = resolved;
  if (typeof raw.homepage === 'string') station.homepage = raw.homepage;
  if (typeof raw.favicon === 'string') station.favicon = raw.favicon;
  if (typeof raw.country === 'string') station.country = raw.country;
  if (typeof raw.countryCode === 'string') station.countryCode = raw.countryCode;
  if (typeof raw.codec === 'string') station.codec = raw.codec;
  if (typeof raw.bitrate === 'number') station.bitrate = raw.bitrate;
  if (typeof raw.lastPlayedAt === 'number') station.lastPlayedAt = raw.lastPlayedAt;
  if (typeof raw.sourceUuid === 'string') station.sourceUuid = raw.sourceUuid;

  return station;
}

/** Postaje iz shrambe; neveljavne se tiho izpustijo, ostale preživijo. */
export function parseStations(raw: unknown): Station[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index) => parseStation(item, index))
    .filter((station): station is Station => station !== null);
}

/* ── Podvajanje ────────────────────────────────────────────────────── */

/**
 * Naslov v obliki, primerni za primerjavo (T2 §7, točka 5).
 *
 * Male črke in brez končne poševnice. Shema se **ne** izenači: `http://x/a`
 * in `https://x/a` sta lahko isti tok, a tudi nista nujno — poenotenje bi
 * uvoz tiho zlilo v napačno postajo.
 */
export function normalizeUrl(url: string): string {
  return url.trim().toLowerCase().replace(/\/+$/, '');
}

/** Ali seznam že vsebuje ta naslov; primerja tudi razrešene naslove. */
export function findByUrl(stations: readonly Station[], url: string): Station | null {
  const target = normalizeUrl(url);
  return (
    stations.find(
      (s) =>
        normalizeUrl(s.url) === target ||
        (s.urlResolved !== undefined && normalizeUrl(s.urlResolved) === target),
    ) ?? null
  );
}

/* ── Ustvarjanje in urejanje ───────────────────────────────────────── */

export interface StationInput {
  name: string;
  url: string;
  tags?: string[];
  country?: string;
  countryCode?: string;
  codec?: string;
  bitrate?: number;
  homepage?: string;
  favicon?: string;
  isHls?: boolean;
  source?: StationSource;
  sourceUuid?: string;
}

/** Žanri so normalizirani na male črke (T2 §2), brez praznih in podvojenih. */
export function normalizeTags(tags: readonly string[] | undefined): string[] {
  const seen = new Set<string>();
  for (const tag of tags ?? []) {
    const clean = tag.trim().toLowerCase();
    if (clean !== '') seen.add(clean);
  }
  return [...seen];
}

export interface CreateStationOptions {
  id: string;
  now: number;
  order: number;
}

/**
 * Nova postaja iz uporabnikovega vnosa.
 *
 * `id`, čas in mesto pridejo od zunaj: `crypto.randomUUID()` in `Date.now()`
 * v domeni bi pomenila, da testa vrstnega reda ni mogoče napisati brez
 * zamrznjene ure.
 */
export function createStation(input: StationInput, options: CreateStationOptions): Station {
  const url = input.url.trim();

  const station: Station = {
    id: options.id,
    name: input.name.trim(),
    url,
    tags: normalizeTags(input.tags),
    isHls: input.isHls === true,
    isHttps: url.startsWith('https:'),
    favorite: false,
    order: options.order,
    addedAt: options.now,
    playCount: 0,
    source: input.source ?? 'manual',
  };

  if (input.country !== undefined) station.country = input.country;
  if (input.countryCode !== undefined) station.countryCode = input.countryCode;
  if (input.codec !== undefined) station.codec = input.codec;
  if (input.bitrate !== undefined) station.bitrate = input.bitrate;
  if (input.homepage !== undefined) station.homepage = input.homepage;
  if (input.favicon !== undefined) station.favicon = input.favicon;
  if (input.sourceUuid !== undefined) station.sourceUuid = input.sourceUuid;

  return station;
}

/* ── Iz kataloga v knjižnico (faza 4) ──────────────────────────────── */

/**
 * Zadetek iz spletne baze v obliko, ki jo sprejme `addStation()` (P2 §2.5).
 *
 * `sourceUuid` se ohrani, ker je edina vez nazaj na vir: brez njega ni mogoče
 * javiti klika (P2 §2.8) niti pozneje dopolniti manjkajočih polj (P2 §1.12).
 *
 * ⚠️ **`urlResolved` se NE prenese.** Vir ga je razrešil ob svojem zadnjem
 * preverjanju, ki je lahko staro mesece. Našo sondo (T4 §3.1) postaja dobi ob
 * prvem predvajanju in tisti zapis je naš. Prevzem tujega bi pomenil, da
 * postaja igra z naslovom, ki ga nismo nikoli preverili — natanko vzrok
 * zatikanja v avtu iz [T2 §12].
 */
export function fromCatalog(station: CatalogStation): StationInput {
  const input: StationInput = {
    name: station.name,
    url: station.url,
    tags: station.tags,
    isHls: station.isHls,
    source: station.source,
    sourceUuid: station.uuid,
  };

  if (station.country !== undefined) input.country = station.country;
  if (station.countryCode !== undefined) input.countryCode = station.countryCode;
  if (station.codec !== undefined) input.codec = station.codec;
  if (station.bitrate !== undefined) input.bitrate = station.bitrate;
  if (station.homepage !== undefined) input.homepage = station.homepage;
  if (station.favicon !== undefined) input.favicon = station.favicon;

  return input;
}

/**
 * Postaja iz seznama, ki ustreza zadetku — sicer `null` (P2 §2.6).
 *
 * Primerja po **naslovu**, ne po `sourceUuid`: ista postaja pride v seznam
 * tudi ročno ali iz varnostne kopije in takrat `sourceUuid` nima. Naslov je
 * tisto, kar dejansko igra — isto merilo kot pri podvajanju (T2 §7, točka 5).
 *
 * Preveri se tudi razrešeni naslov vira: radio-browser marsikatero postajo
 * navaja z naslovom, ki se preusmeri tja, kamor kaže naš `urlResolved`.
 */
export function findCatalogMatch(
  stations: readonly Station[],
  station: CatalogStation,
): Station | null {
  const byUrl = findByUrl(stations, station.url);
  if (byUrl !== null) return byUrl;
  if (station.urlResolved !== undefined) return findByUrl(stations, station.urlResolved);
  return null;
}

/**
 * Obraten pogled od `findCatalogMatch()`: zadetek iz kataloga, ki se ujema z
 * danim naslovom — podlaga za samodejno dopolnjevanje (P2 §1.12).
 *
 * Vir išče po **imenu** (`/json/stations/search`, glej `worker/catalog.ts`) in
 * vrne do strani zadetkov s podobnim imenom. Naslov je tisto, kar med njimi
 * pove, kateri je res ista postaja: ime se rado razlikuje za vejico, naslov pa
 * dejansko igra (isto merilo kot pri podvajanju, T2 §7, točka 5). Zato je
 * ujemanje po naslovu edino dovolj zanesljivo, da smemo iz njega prevzeti polja.
 *
 * Preveri se tudi razrešeni naslov zadetka: vir isto postajo pogosto navaja z
 * naslovom, ki se preusmeri tja, kamor kaže `urlResolved`.
 */
export function findCatalogByUrl(
  catalog: readonly CatalogStation[],
  url: string,
): CatalogStation | null {
  const target = normalizeUrl(url);
  return (
    catalog.find(
      (station) =>
        normalizeUrl(station.url) === target ||
        (station.urlResolved !== undefined && normalizeUrl(station.urlResolved) === target),
    ) ?? null
  );
}

/**
 * Vhod za novo postajo, dopolnjen z zadetkom iz kataloga (P2 §1.12).
 *
 * 🔴 **Zapolni samo manjkajoča polja, vpisanih nikoli ne povozi.** Uporabnik je
 * postajo dodal ročno; kar je vpisal, je merodajno. Prazno polje pomeni »nimam«
 * in ne »izbriši« — dopolnjevanje je usluga in ne popravek. Ime in naslov sta
 * zato nedotakljiva: po naslovu je bil zadetek sploh najden.
 *
 * `sourceUuid` je najpomembnejši prevzem: šele z njim postane ročno dodana
 * postaja **dopolnljiva pozneje in klik javljiv** (P2 §2.8). Vir provenience
 * (`source`) ostane, kar je bil — postajo je dodal uporabnik in ne katalog.
 */
export function enrichInput(input: StationInput, match: CatalogStation): StationInput {
  const next: StationInput = { ...input };

  if (normalizeTags(input.tags).length === 0 && match.tags.length > 0) {
    next.tags = match.tags;
  }
  if (input.countryCode === undefined && match.countryCode !== undefined) {
    next.countryCode = match.countryCode;
  }
  if (input.country === undefined && match.country !== undefined) {
    next.country = match.country;
  }
  if (input.codec === undefined && match.codec !== undefined) {
    next.codec = match.codec;
  }
  // `bitrate` je pri viru že očiščen: 0 pomeni »neznano« in ga `parseCatalogStation`
  // sploh ne zapiše, zato `!== undefined` tu ne prepusti lažne ničle (signal ≠ status).
  if (input.bitrate === undefined && match.bitrate !== undefined) {
    next.bitrate = match.bitrate;
  }
  if (input.homepage === undefined && match.homepage !== undefined) {
    next.homepage = match.homepage;
  }
  if (input.favicon === undefined && match.favicon !== undefined) {
    next.favicon = match.favicon;
  }
  // `isHls` prevzamemo le, kadar vir pravi »da«: HLS tok brez zastavice na iOS
  // tiho obvisi (P2 §0.8), zapisati `false` čez neznano pa ni popravek.
  if (input.isHls === undefined && match.isHls) {
    next.isHls = true;
  }
  if (input.sourceUuid === undefined) {
    next.sourceUuid = match.uuid;
  }

  return next;
}

/** Popravki, ki jih sme narediti obrazec za urejanje. */
export type StationPatch = Partial<
  Pick<
    Station,
    | 'name'
    | 'url'
    | 'tags'
    | 'country'
    | 'countryCode'
    | 'codec'
    | 'bitrate'
    | 'homepage'
    | 'favicon'
  >
>;

/**
 * Postaja s popravki. Ob spremembi naslova **odpade `urlResolved`**:
 * razrešeni naslov je rezultat sonde nad starim naslovom in bi po urejanju
 * tiho predvajal nekaj drugega, kot piše v obrazcu.
 */
export function applyPatch(station: Station, patch: StationPatch): Station {
  const next: Station = { ...station };

  if (patch.name !== undefined) next.name = patch.name.trim();
  if (patch.tags !== undefined) next.tags = normalizeTags(patch.tags);
  if (patch.country !== undefined) next.country = patch.country;
  if (patch.countryCode !== undefined) next.countryCode = patch.countryCode;
  if (patch.codec !== undefined) next.codec = patch.codec;
  if (patch.bitrate !== undefined) next.bitrate = patch.bitrate;
  if (patch.homepage !== undefined) next.homepage = patch.homepage;
  if (patch.favicon !== undefined) next.favicon = patch.favicon;

  if (patch.url !== undefined) {
    const url = patch.url.trim();
    if (normalizeUrl(url) !== normalizeUrl(station.url)) {
      next.url = url;
      next.isHttps = url.startsWith('https:');
      delete next.urlResolved;
    }
  }

  return next;
}

export function upsertStation(stations: readonly Station[], station: Station): Station[] {
  const index = stations.findIndex((s) => s.id === station.id);
  if (index === -1) return [...stations, station];

  const next = [...stations];
  next[index] = station;
  return next;
}

export function removeStation(stations: readonly Station[], id: string): Station[] {
  return renumber(stations.filter((s) => s.id !== id));
}

/**
 * Prednaložene postaje, ki jih v knjižnici ni (T2 §9).
 *
 * Seed se naloži **samo ob prvem zagonu** — prepoznanem po odsotnosti ključa,
 * ne po praznem seznamu. Posledica, ki je do rasti seeda v fazi 3 ni bilo
 * videti: kdor je aplikacijo že odprl, novih prednaloženih postaj **nikoli ne
 * dobi**. Ta funkcija je podlaga za izrecno dejanje v Nastavitvah.
 *
 * ⚠️ **Ne vrača postaj, ki jih uporabnik ima.** Primerja se po `id` in po
 * naslovu: `id` ujame izbrisano prednaloženo, naslov pa isto postajo, ki jo je
 * uporabnik medtem dodal ročno — brez tega bi jo dobil dvakrat.
 *
 * ⚠️ **Obstoječih ne popravlja.** Če je bil naslov prednaložene postaje v
 * novi različici popravljen, uporabnikova kopija ostane stara — sicer bi
 * dejanje povozilo postajo, ki jo je morda sam uredil (T2 §12).
 */
export function missingSeedStations(
  stations: readonly Station[],
  seed: readonly Station[],
): Station[] {
  const ids = new Set(stations.map((s) => s.id));
  const urls = new Set(stations.map((s) => normalizeUrl(s.url)));
  for (const station of stations) {
    if (station.urlResolved !== undefined) urls.add(normalizeUrl(station.urlResolved));
  }

  return seed
    .filter((station) => !ids.has(station.id) && !urls.has(normalizeUrl(station.url)))
    .map((station) => ({ ...station }));
}

/** Doda manjkajoče prednaložene postaje na konec, brez posega v obstoječe. */
export function restoreSeedStations(
  stations: readonly Station[],
  seed: readonly Station[],
): Station[] {
  const missing = missingSeedStations(stations, seed);
  return missing.length === 0 ? [...stations] : renumber([...stations, ...missing]);
}

export function toggleFavorite(stations: readonly Station[], id: string): Station[] {
  return stations.map((s) => (s.id === id ? { ...s, favorite: !s.favorite } : s));
}

/** Zabeleži predvajanje — vir podatka za razvrščanje »nedavno«. */
export function markPlayed(stations: readonly Station[], id: string, now: number): Station[] {
  return stations.map((s) =>
    s.id === id ? { ...s, lastPlayedAt: now, playCount: s.playCount + 1 } : s,
  );
}

/* ── Vrstni red ────────────────────────────────────────────────────── */

/** `order` naj bo vedno 0..n−1 v vrstnem redu polja; luknje se drugače kopičijo. */
export function renumber(stations: readonly Station[]): Station[] {
  return stations.map((station, index) =>
    station.order === index ? station : { ...station, order: index },
  );
}

/**
 * Premik postaje za eno mesto (P2 §1.8, WCAG 2.5.7).
 *
 * Vlečenje **mora** imeti alternativo z enim kazalcem, ta pa je hkrati
 * edina pot za tipkovnico. Zato ni dodatek k vlečenju, ampak osnova —
 * vlečenje se pripne nanjo.
 */
export function moveStation(stations: readonly Station[], id: string, step: 1 | -1): Station[] {
  const ordered = sortByOrder(stations);
  const index = ordered.findIndex((s) => s.id === id);
  if (index === -1) return [...stations];

  const target = index + step;
  if (target < 0 || target >= ordered.length) return [...stations];

  const next = [...ordered];
  const moved = next[index];
  const swapped = next[target];
  if (moved === undefined || swapped === undefined) return [...stations];

  next[index] = swapped;
  next[target] = moved;
  return renumber(next);
}

/** Premik na poljubno mesto — vlečenje in spust. */
export function reorderStation(
  stations: readonly Station[],
  id: string,
  toIndex: number,
): Station[] {
  const ordered = sortByOrder(stations);
  const from = ordered.findIndex((s) => s.id === id);
  if (from === -1) return [...stations];

  const clamped = Math.min(Math.max(toIndex, 0), ordered.length - 1);
  if (clamped === from) return [...stations];

  const next = [...ordered];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return [...stations];
  next.splice(clamped, 0, moved);
  return renumber(next);
}

function sortByOrder(stations: readonly Station[]): Station[] {
  return [...stations].sort((a, b) => a.order - b.order);
}

/* ── Prikaz: razvrščanje in filtriranje ────────────────────────────── */

/**
 * Razvrščanje po meri je **privzeto** in ostane uporabnikovo: `order`.
 * Priljubljene se ne dvigajo na vrh — to bi uporabnikov vrstni red tiho
 * prepisalo. Priljubljene so filter (P2 §1.3), ne razvrstitev.
 */
export function sortStations(stations: readonly Station[], mode: SortMode, lang: Lang): Station[] {
  if (mode === 'custom') return sortByOrder(stations);

  if (mode === 'recent') {
    // Nikoli predvajana gre za predvajane, ne pred nje: `0` kot »nikoli«.
    return [...stations].sort((a, b) => (b.lastPlayedAt ?? 0) - (a.lastPlayedAt ?? 0));
  }

  // `Intl.Collator`, ne `<`: „Č" mora stati za „C", ne za „Z" (P4 §7).
  const collator = new Intl.Collator(lang, { sensitivity: 'base', numeric: true });
  const sorted = [...stations].sort((a, b) => collator.compare(a.name, b.name));
  return mode === 'za' ? sorted.reverse() : sorted;
}

/** Iskanje po lastnem seznamu (P2 §1.6): ime, žanri, država. */
export function matchesQuery(station: Station, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === '') return true;

  const haystack = [station.name, station.country ?? '', ...station.tags].join(' ').toLowerCase();
  return haystack.includes(needle);
}

export interface StationView {
  sortMode: SortMode;
  favoritesOnly: boolean;
  query: string;
  lang: Lang;
}

/**
 * Seznam, kot ga uporabnik dejansko vidi.
 *
 * ⚠️ **Prejšnja / naslednja postaja se ravnata po tem seznamu, ne po celem**
 * (P2 §0.4). Če uporabnik gleda samo priljubljene, ⏭ ne sme skočiti na
 * postajo, ki je na zaslonu ni.
 */
export function visibleStations(stations: readonly Station[], view: StationView): Station[] {
  const filtered = stations.filter(
    (station) => (!view.favoritesOnly || station.favorite) && matchesQuery(station, view.query),
  );
  return sortStations(filtered, view.sortMode, view.lang);
}
